import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useWorkspaceId } from "../bridges/session";
import { api } from "../data/client";
import { scopedKey } from "../data/keys";
import type { ContactBody, ContactReceipt, Holder, InboxFilter, InboxMeta, StaffHandler, StaffMessage, Urgency } from "./types";

const N = "/api/auth/next";

/** Signed in: the server fills in the person, the account name and the role. Public (sign-in and sign-up pages): the open, throttled call. */
export const useSendContact = (mode: "member" | "public") =>
  useMutation({
    mutationFn: async (body: ContactBody) =>
      (await api.post<{ data: ContactReceipt }>(mode === "member" ? `${N}/contact` : "/api/contact-us", body, { quietServerError: true })).data,
  });

/** The locked-account page: who holds the account. A person who is not the Owner gets the Owner's details; the Owner gets `is_owner`. */
export const useHolder = (enabled = true) => {
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: scopedKey(ws, "contact", "holder"),
    queryFn: async () => (await api.get<{ data: Holder }>(`${N}/contact/holder`)).data,
    enabled: ws !== null && enabled,
    staleTime: 60_000,
    retry: false,
  });
};

const inboxRoot = (ws: number | null) => scopedKey(ws, "staff-messages");
export type InboxQuery = { filter: InboxFilter; q: string; page: number };

export const useMessages = (query: InboxQuery) => {
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: [...inboxRoot(ws), "list", query],
    queryFn: async () => {
      const r = await api.get<{ data: StaffMessage[]; meta: InboxMeta }>(`${N}/staff/messages`, { filter: query.filter, q: query.q || undefined, page: query.page });
      return { rows: r.data, meta: r.meta };
    },
    enabled: ws !== null,
    staleTime: 10_000,
    retry: false,
  });
};

/** The number beside Messages in the staff menu. */
export const useOpenMessageCount = (enabled = true) => {
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: [...inboxRoot(ws), "count"],
    queryFn: async () => (await api.get<{ data: StaffMessage[]; meta: InboxMeta }>(`${N}/staff/messages`, { filter: "open", page: 1 })).meta.counts.open,
    enabled: ws !== null && enabled,
    staleTime: 30_000,
    retry: false,
  });
};

export const useHandlers = () => {
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: [...inboxRoot(ws), "handlers"],
    queryFn: async () => (await api.get<{ data: StaffHandler[] }>(`${N}/staff/messages/handlers`)).data,
    enabled: ws !== null,
    staleTime: 5 * 60_000,
    retry: false,
  });
};

export type MessageChange = { assigned_to_user_id?: number | null; urgency?: Urgency; answer_by?: string | null };

export const useMessageActions = () => {
  const ws = useWorkspaceId();
  const client = useQueryClient();
  const done = () => client.invalidateQueries({ queryKey: inboxRoot(ws) });
  const change = useMutation({
    mutationFn: async (v: { id: number; change: MessageChange }) => (await api.patch<{ data: StaffMessage }>(`${N}/staff/messages/${v.id}`, v.change)).data,
    onSuccess: done,
  });
  const handle = useMutation({ mutationFn: async (id: number) => (await api.post<{ data: StaffMessage }>(`${N}/staff/messages/${id}/handle`)).data, onSuccess: done });
  const reopen = useMutation({ mutationFn: async (id: number) => (await api.post<{ data: StaffMessage }>(`${N}/staff/messages/${id}/reopen`)).data, onSuccess: done });
  return { change, handle, reopen };
};
